import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { AdvancedMarker, useMap, useApiIsLoaded } from "@vis.gl/react-google-maps";
import RenderIcon from "./RenderIcon";
import { useWebSocket } from "context/WebSocketContext";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { Card } from "@mui/material";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Polyline from "../Polyline";

const ClusteredDeviceMarker = ({ devices = [], deviceType, selectedRow, onSelectRow, resetSelectedeDevice }) => {
    const [markers, setMarkers] = useState({});
    const [selectedTreeKey, setSelectedTreeKey] = useState(null);
    const [deviceData, setDeviceData] = useState({});
    const zoomRef = useRef(null);
    const { assetTrackerData } = useWebSocket();
    const map = useMap();
    const clustererRef = useRef(null);
    const isInitialLoad = useRef(true);
    const isInitialMarker = useRef(false);
    const prevAssetTrackerData = useRef(null);
    const markersRef = useRef({})

    // Calculate adjusted position for a single device
    const getAdjustedPosition = useCallback((deviceId, newPosition) => {
        const positionMap = {};
        const adjustedPositions = {};

        devices.forEach((device) => {
            const pos = device.id === deviceId ? newPosition : (deviceData[device.id]?.position || { lat: device.lat, lng: device.lng });
            const key = `${pos.lat},${pos.lng}`;
            if (!positionMap[key]) positionMap[key] = [];
            positionMap[key].push(device.id);
        });

        Object.keys(positionMap).forEach((key) => {
            const [lat, lng] = key.split(",").map(parseFloat);
            const deviceIds = positionMap[key];
            deviceIds.forEach((id, index) => {
                const offsetLng = lng + index * 0.00005;
                adjustedPositions[id] = { lat, lng: offsetLng };
            });
        });

        return adjustedPositions[deviceId] || newPosition;
    }, [devices, deviceData]);

    // Initialize map and clusterer
    useEffect(() => {
        if (!map) return;

        if (!clustererRef.current) {
            clustererRef.current = new MarkerClusterer({ map, algorithm: new SuperClusterAlgorithm({ maxZoom: 20 }) });
        }
        if (selectedRow) {
            return
        }
        if (devices.length >= 1) {
            setSelectedTreeKey(null);
            onSelectRow(null);
            isInitialMarker.current = false;
        }
        if (isInitialLoad.current && devices.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            devices.forEach((device) => {
                const { lat, lng } = deviceData[device.id]?.position || {
                    lat: device.lat,
                    lng: device.lng,
                };;
                if (
                    typeof lat === 'number' && typeof lng === 'number' &&
                    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180
                ) {
                    bounds.extend({ lat, lng });
                } else {
                    console.warn('Invalid coordinates:', device);
                }
            });
            setTimeout(() => {
                map.fitBounds(bounds);
            }, 200); // adjust delay as needed
            isInitialLoad.current = false;
        }

        if (!isInitialLoad.current && devices.length > 0) {
            renderMarker();
        }

        return () => {
            if (clustererRef.current) {
                clustererRef.current.clearMarkers();
            }
        };
    }, [map, devices]);

    // Handle WebSocket updates
    useEffect(() => {
        if (!assetTrackerData || !map || !clustererRef.current) return;
        if (prevAssetTrackerData.current === assetTrackerData) return;
        prevAssetTrackerData.current = assetTrackerData;

        const { device_id, data } = assetTrackerData;
        if (!device_id || !data) return;

        const { position_latitude, position_longitude, moment_status, position_direction } = data;
        const lat = parseFloat(position_latitude);
        const lng = parseFloat(position_longitude);

        if (isNaN(lat) || isNaN(lng)) return;

        const newPosition = { lat, lng };
        const adjustedPosition = getAdjustedPosition(device_id, newPosition);
        const newAnimate = moment_status === "Moving";
        const newHeading = position_direction || 0;

        setDeviceData((prev) => {
            const prevDevice = prev[device_id] || {};
            if (
                prevDevice.position?.lat === adjustedPosition.lat &&
                prevDevice.position?.lng === adjustedPosition.lng &&
                prevDevice.animate === newAnimate &&
                prevDevice.heading === newHeading
            ) {
                return prev;
            }
            return {
                ...prev,
                [device_id]: {
                    position: adjustedPosition,
                    animate: newAnimate,
                    heading: newHeading,
                },
            };
        });

        // Update specific marker's position directly if it exists
        if (markers[device_id]) {
            const marker = markers[device_id];
            const needsClusterUpdate =
                marker.position.lat !== adjustedPosition.lat ||
                marker.position.lng !== adjustedPosition.lng;

            if (needsClusterUpdate) {
                clustererRef.current.removeMarker(marker);
            }
        }
    }, [assetTrackerData, map, getAdjustedPosition]);

    // Update clusterer when markers change (initial load or device list changes)
    useEffect(() => {
        if (!clustererRef.current || !map || isInitialMarker.current) return;
        renderMarker();
    }, [markers, map]);

    function renderMarker() {
        if (!clustererRef.current) return;
        // Gather all marker instances that are truthy
        let markerValues = Object.values(markers).filter(Boolean);
        // Ensure each marker is a valid Google Maps marker (has setMap function)
        const validMarkers = markerValues.filter(m => m && typeof m.setMap === 'function');
        clustererRef.current.clearMarkers();
        if (validMarkers.length > 0 && validMarkers.length === devices.length) {
            clustererRef.current.addMarkers(validMarkers);
            markersRef.current = validMarkers;
            isInitialMarker.current = true;
        } else if (markersRef.current.length > 0 && markersRef.current.length === devices.length) {
            // Fallback to previously stored valid markers
            const fallbackValid = markersRef.current.filter(m => m && typeof m.setMap === 'function');
            clustererRef.current.addMarkers(fallbackValid);
            isInitialMarker.current = true;
        }
    }

    // Handle selected device
    const selectedDevice = useMemo(
        () => devices.find((t) => t.id === selectedTreeKey) || null,
        [devices, selectedTreeKey]
    );

    // Center map on selected device
    useEffect(() => {
        if (selectedDevice && map) {
            const zoomLevel = map.getZoom();
            zoomRef.current = zoomLevel;
            const mainPos = deviceData[selectedDevice.id]?.position || {
                lat: selectedDevice.lat,
                lng: selectedDevice.lng,
            };
            if (
                selectedDevice.has_linked_device &&
                selectedDevice.linked_devices?.length
            ) {

                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(mainPos);
                selectedDevice.linked_devices.forEach(ld => {
                    bounds.extend({
                        lat: ld.entity_address_item.latitude,
                        lng: ld.entity_address_item.longitude,
                    });
                });


                map.fitBounds(bounds, 100); // 100px padding
            } else {
                map.setCenter(mainPos);
                map.setZoom(18);
            }
        }
    }, [selectedDevice, deviceData, map]);

    useEffect(() => {
        if (deviceType) {
            zoomRef.current = null;
            setSelectedTreeKey(null)
            // map.setZoom(12);
        }
    }, [deviceType]);

    const setMarkerRef = useCallback((marker, key) => {
        setMarkers((prevMarkers) => {
            if (!marker) {
                const { [key]: _, ...newMarkers } = prevMarkers;
                return newMarkers;
            }
            return { ...prevMarkers, [key]: marker };
        });
    }, []);

    const handleInfoWindowClose = useCallback(() => {
        resetSelectedeDevice()
        setSelectedTreeKey(null);
        onSelectRow(null);
        if (zoomRef.current && map) {
            map.setZoom(zoomRef.current);
        }
        // isInitialLoad.current = true
    }, [map, selectedRow]);

    const handleMarkerClick = useCallback((tree) => {
        setSelectedTreeKey(tree.id);
    }, []);

    useEffect(() => {
        if (selectedRow) {
            handleMarkerClick(selectedRow)
        }
    }, [selectedRow])

    // Memoize marker data
    const markerData = useMemo(() => {
        return devices.map((device) => ({
            device,
            position: deviceData[device.id]?.position || { lat: device.lat, lng: device.lng },
            animate: deviceData[device.id]?.animate ?? device.animate,
            heading: deviceData[device.id]?.heading ?? device.heading,
        }));
    }, [devices, deviceData]);

    const formatAddress = (address) =>
        [
            address?.street_address1,
            address?.street_address2,
            address?.city,
            address?.state,
            address?.postal_code,
            address?.country,
        ]
            .filter(Boolean)
            .join(", ");

    return (
        <>
            {markerData.map(({ device, position, animate, heading }) => (
                <AdvancedMarkerWithRef
                    key={`${device.id}-${position.lat.toFixed(6)}-${position.lng.toFixed(6)}`}
                    device={device}
                    animate={animate}
                    heading={heading}
                    position={position}
                    onClick={handleMarkerClick}
                    setMarkerRef={setMarkerRef}
                />
            ))}
            {selectedDevice && !selectedRow?.click_type && (
                <Card
                    sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                        padding: 2,
                        backgroundColor: "white",
                    }}
                >
                    <MDTypography sx={{ fontSize: "20px" }}>
                        {selectedDevice.attributes.device_data.name} #{selectedDevice.attributes.device_id}
                    </MDTypography>
                    <MDTypography sx={{ fontSize: "15px" }}>
                        Address: {formatAddress(selectedDevice.attributes?.entity_address_item)}
                    </MDTypography>
                    {[
                        "moment_status",
                        "moment_status_duration",
                        "position_speed",
                        "position_direction",
                        "position_latitude",
                        "position_longitude",
                        "position_altitude",
                        "position_satellites",
                        "vehicle_mileage",
                        "gsm_signal_level",
                    ].map((key) => (
                        <MDTypography key={key} sx={{ fontSize: "15px" }}>
                            {key.replaceAll("_", " ").replaceAll(/\b\w/g, (c) => c.toUpperCase())}:{" "}
                            {selectedDevice.attributes?.device_value?.[key]}
                        </MDTypography>
                    ))}
                    <MDButton color="dark" onClick={() => handleInfoWindowClose()}>
                        Close
                    </MDButton>
                </Card>
            )}
            {/* Linked devices + animated lines */}
            {selectedDevice?.linked_devices?.map((ld, i) => {
                const linkedPos = {
                    lat: ld.entity_address_item.latitude,
                    lng: ld.entity_address_item.longitude,
                };

                const RenderIcon =
                    () => {
                        // const iconStyle = {
                        //     color: device.color || "#000",
                        // };

                        return (
                            <div className="icon-wrapper" style={{ color: "#fff" }} title={ld.device_name ?? ""}>
                                <h6
                                    style={{
                                        position: "absolute",
                                        top: "-17px",
                                        color: "#000",
                                        whiteSpace: "nowrap",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                    }}
                                >
                                    {ld.device_name || ""}
                                </h6>
                                <i
                                    className={ld.entity_address_item.class_name || "fas fa-map-pin"}
                                    style={{ fontSize: "24px" }}
                                ></i>
                            </div>
                        );
                    }

                return (
                    <React.Fragment key={i}>
                        <AdvancedMarker position={linkedPos} >
                            <RenderIcon />
                        </AdvancedMarker>
                    </React.Fragment>
                );
            })}
        </>
    );
};

// Memoized marker component
const AdvancedMarkerWithRef = React.memo(
    ({ device, animate, position, heading, onClick, setMarkerRef }) => {
        const handleClick = useCallback(() => onClick(device), [onClick, device]);
        const ref = useCallback((marker) => setMarkerRef(marker, device.id), [
            setMarkerRef,
            device.id,
        ]);

        return (
            <AdvancedMarker position={position} ref={ref} onClick={handleClick}>
                <RenderIcon device={device} animate={animate} heading={heading} />
            </AdvancedMarker>
        );
    },
    () => false // ⚠️ Never skip render
);

function LinkedCardView({ data }) {
    return (
        data.device_type_id === 8 ?
            <>
                <MDTypography sx={{ fontSize: "20px" }}>
                    {data?.device_id} / {data?.device_name}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Device Status: {data?.moment_status}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Speed: {data?.location?.speed_mph} mph
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Heading: {data?.location?.heading}°
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Latitude: {data?.location?.latitude}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Longitude: {data?.location?.longitude}
                </MDTypography>
            </> :
            <>
                <MDTypography sx={{ fontSize: "20px" }}>
                    {data?.device_id} / {data?.device_name}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Pressure (PSI): {data?.pressure_psi}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Percent Level: {data?.level_percent}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Volume: {data?.volume_gallons} Gallons
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Latitude: {data?.entity_address_item?.latitude}
                </MDTypography>
                <MDTypography sx={{ fontSize: "15px" }}>
                    Longitude: {data?.entity_address_item?.longitude}
                </MDTypography>
            </>
    )
}
export default ClusteredDeviceMarker;